import { ISectionDefinitionData } from 'types/order/fees/ISectionDefinition';
import { ISettlementAndFeeCategoryDefinitionData } from 'types/order/fees/ISettlementAndFeeCategoryDefinition';
import { ISectionData, ISettlementItemData } from 'types/order/fees/ISectionData';
import { sortByCreatedAt } from './SortByCreatedAt';

export interface IFeeSection {
  sectionDefinition: ISectionDefinitionData;
  sectionData?: ISectionData;
  settlementItems: ISettlementItemData[];
  feeDefinitions: ISettlementAndFeeCategoryDefinitionData[];
  options: ISettlementAndFeeCategoryDefinitionData[];
}

export interface IOrganizeFeesProps {
  sectionDefinitionData: ISectionDefinitionData[];
  sectionData: ISectionData[];
}

export const organizeFees = ({ sectionDefinitionData, sectionData }: IOrganizeFeesProps): IFeeSection[] => {
  // Map to make a new array so we don't sort the original. Sorting is in place on arrays.
  const sortedSectionDefinitions = sectionDefinitionData
    .map((t) => ({
      ...t,
      statementSettlementAndFeeCategoryDefinitions: t.statementSettlementAndFeeCategoryDefinitions
        .map((def) => def)
        .sort((a, b) => a.feeCategorySeq - b.feeCategorySeq),
    }))
    .sort((a, b) => a.sectionSeq - b.sectionSeq);

  const groupedSections: IFeeSection[] = sortedSectionDefinitions.map((sectionDefinition) => {
    const feeDefinitions = sectionDefinition.statementSettlementAndFeeCategoryDefinitions;
    const options = feeDefinitions.filter((t) => t.editable);

    const section = sectionData.find(
      (t) => t.statementSectionDefinitionId === sectionDefinition.statementSectionDefinitionId
    );

    const settlementItems = feeDefinitions
      .map((t) =>
        section
          ? section.statementSettlementItems.filter(
              (item) =>
                item.statementSettlementAndFeeCategoryDefinitionId === t.statementSettlementAndFeeCategoryDefinitionId
            )
          : []
      )
      .map((t) => t.sort(sortByCreatedAt))
      .reduce((p, c) => [...p, ...c], [])
      .map((item) => ({
        ...item,
        statementFees: item.statementFees.map((t) => t).sort(sortBySequence(feeDefinitions)),
      }));

    const feeSection: IFeeSection = {
      sectionDefinition: sectionDefinition,
      sectionData: sectionData.find(
        (t) => t.statementSectionDefinitionId === sectionDefinition.statementSectionDefinitionId
      )!,
      feeDefinitions,
      options,
      settlementItems,
    };
    return feeSection;
  });

  return groupedSections;
};

export const sortBySequence =
  (x: { statementSettlementAndFeeCategoryDefinitionId: string; feeSeq: number }[]) =>
  (
    a: { statementSettlementAndFeeCategoryDefinitionId: string; createdAt: string },
    b: { statementSettlementAndFeeCategoryDefinitionId: string; createdAt: string }
  ) => {
    const aDef =
      x.find((t) => t.statementSettlementAndFeeCategoryDefinitionId === a.statementSettlementAndFeeCategoryDefinitionId)
        ?.feeSeq || 0;
    const bDef =
      x.find((t) => t.statementSettlementAndFeeCategoryDefinitionId === b.statementSettlementAndFeeCategoryDefinitionId)
        ?.feeSeq || 0;
    if (aDef === bDef) {
      return sortByCreatedAt(a, b);
    }
    return aDef - bDef;
  };
